<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">State Reports</h1>
        <p class="text-muted">Manage state compliance reports across all organizations</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-outline-secondary" (click)="loadQueueStats(); loadReports()">
          <i class="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- Queue Stats Summary -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title mb-3 d-flex align-items-center">
              <i class="bi bi-speedometer2 me-2"></i>
              Queue Statistics
              @if (fetchingQueueStats) {
                <span class="ms-2 text-muted small d-inline-flex align-items-center">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Fetching...
                </span>
              }
            </h6>
            <div class="row g-3">
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-warning">{{ queueStats?.pending ?? '-' }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Pending</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-info">{{ queueStats?.active ?? '-' }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Active</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-success">{{ queueStats?.completed ?? '-' }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Completed</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-danger">{{ queueStats?.failed ?? '-' }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Failed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Batch Generation Section -->
    <div class="card mb-4">
      <div class="card-header batch-header cursor-pointer user-select-none" (click)="toggleBatchSection()">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="fw-semibold mb-0">
            <i class="bi bi-collection me-2"></i>
            Generate Batch Reports
          </h6>
          <button type="button" class="btn btn-sm btn-link text-body-secondary text-decoration-none p-0">
            <i class="bi" [ngClass]="batchSectionExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </button>
        </div>
      </div>

      <div class="collapse" [class.show]="batchSectionExpanded">
        <div class="card-body">
          <div class="row">
            <!-- Left Column: Organization Selection -->
            <div class="col-md-7">
              <h6 class="text-muted mb-3">
                <i class="bi bi-building me-2"></i>
                Select Organizations
                <span *ngIf="selectedOrganizations.size > 0" class="badge bg-primary ms-2">
                  {{ selectedOrganizations.size }} selected
                </span>
              </h6>

              <!-- Organization Search -->
              <div class="input-group mb-3">
                <span class="input-group-text">
                  <i class="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Filter organizations..."
                  [(ngModel)]="batchOrganizationFilter"
                />
              </div>

              <!-- Selection Buttons -->
              <div class="mb-3">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-2"
                  (click)="selectAllFiltered()"
                >
                  <i class="bi bi-check-all me-1"></i>
                  Select All (filtered)
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  (click)="clearSelection()"
                  [disabled]="selectedOrganizations.size === 0"
                >
                  <i class="bi bi-x-circle me-1"></i>
                  Clear Selection
                </button>
              </div>

              <!-- Organization List -->
              <div class="batch-org-list overflow-y-auto border rounded">
                <table class="table table-sm table-hover mb-0">
                  <thead class="sticky-top">
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>Organization</th>
                      <th>States</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredBatchOrganizations.length === 0">
                      <td colspan="3" class="text-center text-muted py-3">
                        No organizations found
                      </td>
                    </tr>
                    <tr
                      *ngFor="let org of filteredBatchOrganizations"
                      (click)="toggleOrganizationSelection(org.id)"
                      class="cursor-pointer"
                      [class.table-active]="isOrganizationSelected(org.id)"
                    >
                      <td class="py-2">
                        <div class="form-check mb-0">
                          <input
                            class="form-check-input cursor-pointer"
                            type="checkbox"
                            [checked]="isOrganizationSelected(org.id)"
                            (click)="$event.stopPropagation()"
                            (change)="toggleOrganizationSelection(org.id)"
                          />
                        </div>
                      </td>
                      <td class="py-2">{{ org.name }}</td>
                      <td class="py-2">
                        <ng-container *ngIf="isOrganizationSelected(org.id)">
                          <!-- Loading state -->
                          <span *ngIf="isLoadingOrgStates(org.id)" class="text-muted">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Loading...
                          </span>

                          <!-- States loaded -->
                          <ng-container *ngIf="!isLoadingOrgStates(org.id)">
                            <span
                              *ngFor="let state of getOrganizationStatesFromCache(org.id)"
                              class="badge bg-light text-dark border me-1 mb-1"
                            >
                              {{ state }}
                            </span>
                            <span
                              *ngIf="getOrganizationStatesFromCache(org.id).length === 0"
                              class="text-muted small"
                            >
                              No states
                            </span>
                          </ng-container>
                        </ng-container>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Total Report Count -->
              <div class="mt-3 p-2 rounded bg-body-tertiary small" *ngIf="selectedOrganizations.size > 0">
                <i class="bi bi-info-circle me-2"></i>
                <strong>{{ getTotalReportCount() }}</strong> reports will be generated
                for <strong>{{ selectedOrganizations.size }}</strong> organization(s)
                across <strong>{{ getUniqueStateCount() }}</strong> unique state(s)
              </div>
            </div>

            <!-- Right Column: Options -->
            <div class="col-md-5">
              <h6 class="text-muted mb-3">
                <i class="bi bi-gear me-2"></i>
                Report Options
              </h6>

              <!-- Date Range -->
              <div class="mb-3">
                <label class="form-label small text-body-secondary">Date Range</label>
                <div class="row g-2">
                  <div class="col-6">
                    <input
                      type="date"
                      class="form-control"
                      [(ngModel)]="batchStartDate"
                      [max]="batchEndDate"
                    />
                    <small class="text-muted">Start Date</small>
                  </div>
                  <div class="col-6">
                    <input
                      type="date"
                      class="form-control"
                      [(ngModel)]="batchEndDate"
                      [min]="batchStartDate"
                    />
                    <small class="text-muted">End Date</small>
                  </div>
                </div>
                <div *ngIf="!isBatchDateRangeValid() && batchStartDate && batchEndDate" class="text-danger small mt-1">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  End date must be on or after start date
                </div>
              </div>

              <!-- Report Type -->
              <div class="mb-3">
                <label class="form-label small text-body-secondary">Report Type</label>
                <select class="form-select" [(ngModel)]="batchReportType">
                  <option *ngFor="let opt of reportTypeOptions" [value]="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <!-- Batch Limit Warning -->
              <div *ngIf="showBatchLimitWarning" class="alert alert-warning small mb-3">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                You have selected more than {{ BATCH_LIMIT_WARNING }} organizations.
                Large batches may take longer to process.
              </div>

              <!-- Generate Button -->
              <div class="d-grid">
                <button
                  type="button"
                  class="btn btn-primary"
                  (click)="confirmBatchGeneration()"
                  [disabled]="!canGenerateBatch()"
                >
                  <i class="bi bi-play-fill me-2"></i>
                  Generate Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Search -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by report name..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Organization Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Organization</label>
            <select class="form-select" [(ngModel)]="filterOrganization" (change)="onFilterChange()">
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
            </select>
          </div>

          <!-- State Filter -->
          <div class="col-md-2">
            <label for="filterState" class="form-label small text-body-secondary">State</label>
            <app-us-state-select
              inputId="filterState"
              placeholder="All States"
              [(ngModel)]="filterState"
              (ngModelChange)="onFilterChange()"
            ></app-us-state-select>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus" (change)="onFilterChange()">
              <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
            </select>
          </div>

          <!-- Date Range -->
          <div class="col-md">
            <label class="form-label small text-body-secondary">Created From</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterStartDate"
              (change)="onFilterChange()"
            />
          </div>

          <div class="col-md">
            <label class="form-label small text-body-secondary">Created To</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterEndDate"
              (change)="onFilterChange()"
            />
          </div>
        </div>

        @if (showClearFilters()) {
          <div class="row mt-3">
            <div class="col-12">
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearFilters()">
                <i class="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Reports Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>Report Name</th>
              <th>Organization</th>
              <th>State</th>
              <th>Date Range</th>
              <th>Status</th>
              <th>Created</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="reports.length === 0">
              <td colspan="7" class="text-center text-muted py-4">
                No reports found
              </td>
            </tr>
            <tr *ngFor="let report of reports">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar entity-avatar-square">
                    <i class="bi bi-file-earmark-text"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ report.name }}</div>
                    <small class="text-muted">{{ getReportTypeLabel(report.reportType) }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span>{{ report.organizationName || getOrganizationName(report.organizationId) }}</span>
              </td>
              <td>
                <span class="badge bg-light text-dark">{{ report.state }}</span>
              </td>
              <td>
                <small class="text-muted">{{ formatDateRange(report.startDate, report.endDate) }}</small>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusClass(report.status)">
                  <i class="bi" [class]="getStatusIcon(report.status)"></i>
                  {{ getStatusLabel(report.status) }}
                </span>
                <div *ngIf="report.status === 'generating'" class="progress mt-1" style="height: 4px;">
                  <div class="progress-bar progress-bar-striped progress-bar-animated"
                       [style.width.%]="report.generationProgress">
                  </div>
                </div>
              </td>
              <td>
                <small class="text-muted">{{ formatDate(report.createdAt) }}</small>
              </td>
              <td class="text-end">
                <!-- View Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  (click)="viewReport(report)"
                  title="View Details"
                >
                  <i class="bi bi-eye"></i>
                </button>

                <!-- Retry Button (only for failed) -->
                <button
                  *ngIf="canRetry(report)"
                  type="button"
                  class="btn btn-sm btn-outline-warning me-1"
                  (click)="retryReport(report)"
                  [disabled]="actionLoading"
                  title="Retry Failed Job"
                >
                  <i class="bi bi-arrow-repeat"></i>
                </button>

                <!-- Regenerate Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-info me-1"
                  (click)="confirmRegenerate(report)"
                  [disabled]="actionLoading"
                  title="Regenerate Report"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>

                <!-- Delete Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  (click)="confirmDelete(report)"
                  [disabled]="!canDelete(report) || actionLoading"
                  [title]="canDelete(report) ? 'Delete Report' : 'Cannot delete while generating'"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalReports) }} of {{ totalReports }} reports
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Report Detail Modal -->
<div class="modal fade" id="detailModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-file-earmark-text me-2"></i>Report Details
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="closeDetailModal()"></button>
      </div>
      <div class="modal-body" *ngIf="selectedReport">
        <!-- Report Metadata -->
        <div class="row mb-4">
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Report Information</h6>
            <dl class="row mb-2">
              <dt class="col-5 fw-normal text-body-secondary">Name</dt><dd class="col-7 fw-medium">{{ selectedReport.name }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Organization</dt><dd class="col-7">{{ selectedReport.organizationName || getOrganizationName(selectedReport.organizationId) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">State</dt><dd class="col-7">{{ getStateName(selectedReport.state) }} ({{ selectedReport.state }})</dd>
              <dt class="col-5 fw-normal text-body-secondary">Report Type</dt><dd class="col-7">{{ getReportTypeLabel(selectedReport.reportType) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Date Range</dt><dd class="col-7">{{ formatDateRange(selectedReport.startDate, selectedReport.endDate) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Created</dt><dd class="col-7">{{ formatDate(selectedReport.createdAt) }}</dd>
            </dl>
          </div>
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Status</h6>
            <div class="mb-3">
              <span class="badge" [ngClass]="getStatusClass(selectedReport.status)">
                <i class="bi" [class]="getStatusIcon(selectedReport.status)"></i>
                {{ getStatusLabel(selectedReport.status) }}
              </span>
            </div>

            <!-- Progress bar for generating -->
            <div *ngIf="selectedReport.status === 'generating'" class="mb-3">
              <label class="form-label small text-muted">Generation Progress</label>
              <div class="progress" style="height: 20px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
                     [style.width.%]="selectedReport.generationProgress">
                  {{ selectedReport.generationProgress }}%
                </div>
              </div>
            </div>

            <!-- Report stats for completed -->
            <div *ngIf="selectedReport.status === 'ready' || selectedReport.status === 'submitted'">
              <dl class="row mb-2">
                <ng-container *ngIf="selectedReport.recordCount !== null"><dt class="col-5 fw-normal text-body-secondary">Records</dt><dd class="col-7">{{ selectedReport.recordCount }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.fileSize"><dt class="col-5 fw-normal text-body-secondary">File Size</dt><dd class="col-7">{{ selectedReport.fileSize }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedAt"><dt class="col-5 fw-normal text-body-secondary">Submitted</dt><dd class="col-7">{{ formatDate(selectedReport.submittedAt) }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedTo"><dt class="col-5 fw-normal text-body-secondary">Submitted To</dt><dd class="col-7">{{ selectedReport.submittedTo }}</dd></ng-container>
              </dl>
            </div>

            <!-- Error message for failed -->
            <div *ngIf="selectedReport.status === 'failed' && selectedReport.errorMessage" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Error:</strong> {{ selectedReport.errorMessage }}
            </div>
          </div>
        </div>

        <!-- Report Options -->
        <div class="mb-4" *ngIf="selectedReport.options">
          <h6 class="text-muted mb-2">Report Options</h6>
          <div class="row g-2">
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeChemicalDetails ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeChemicalDetails ? 'bi-check' : 'bi-x'"></i>
                Chemical Details
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeApplicationRates ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeApplicationRates ? 'bi-check' : 'bi-x'"></i>
                Application Rates
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeFieldMaps ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeFieldMaps ? 'bi-check' : 'bi-x'"></i>
                Field Maps
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeWeatherConditions ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeWeatherConditions ? 'bi-check' : 'bi-x'"></i>
                Weather Conditions
              </span>
            </div>
          </div>
        </div>

        <!-- Job Information -->
        <div *ngIf="selectedReport.jobs && selectedReport.jobs.length > 0">
          <h6 class="text-muted mb-2">Job History</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let job of selectedReport.jobs">
                  <td><code class="small bg-body-tertiary rounded px-1">{{ job.id }}</code></td>
                  <td>
                    <span class="badge bg-secondary">{{ job.status }}</span>
                  </td>
                  <td>{{ job.attemptsMade }}</td>
                  <td>{{ formatTimestamp(job?.createdAt ?? job?.timestamp) }}</td>
                  <td>{{ formatTimestamp(job?.finishedAt ?? job?.finishedOn) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div *ngIf="selectedReport.jobs[0]?.failedReason" class="alert alert-danger mt-2">
            <strong>Failure Reason:</strong> {{ selectedReport.jobs[0].failedReason }}
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          *ngIf="selectedReport?.reportUrl"
          type="button"
          class="btn btn-primary"
          (click)="openReportUrl()"
        >
          <i class="bi bi-download me-2"></i>
          Download Report
        </button>
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="closeDetailModal()">
          Close
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div class="modal fade" id="deleteModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>
          Confirm Delete
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelDelete()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToDelete">
        <p>Are you sure you want to delete the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToDelete.name }}</strong>
            <br>
            <small class="text-muted">{{ reportToDelete.organizationName || getOrganizationName(reportToDelete.organizationId) }} - {{ reportToDelete.state }}</small>
          </div>
        </div>
        <div class="alert alert-warning mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          This action cannot be undone. The report will be permanently deleted.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelDelete()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="executeDelete()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Delete Report
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Regenerate Confirmation Modal -->
<div class="modal fade" id="regenerateModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-arrow-clockwise text-info me-2"></i>
          Confirm Regenerate
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelRegenerate()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToRegenerate">
        <p>Are you sure you want to regenerate the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToRegenerate.name }}</strong>
            <br>
            <small class="text-muted">{{ reportToRegenerate.organizationName || getOrganizationName(reportToRegenerate.organizationId) }} - {{ reportToRegenerate.state }}</small>
          </div>
        </div>
        <div class="alert alert-info mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          A new generation job will be created. This will overwrite any existing report file.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelRegenerate()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-info"
          (click)="executeRegenerate()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Regenerate Report
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Batch Generation Confirmation Modal -->
<div class="modal fade" id="batchConfirmModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-collection text-primary me-2"></i>
          Confirm Batch Generation
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelBatchConfirmation()"></button>
      </div>
      <div class="modal-body">
        <p>You are about to generate state reports with the following parameters:</p>

        <div class="card mb-3">
          <div class="card-body py-3">
            <div class="row">
              <div class="col-6">
                <div class="text-muted small">Organizations</div>
                <div class="fw-bold">{{ selectedOrganizations.size }}</div>
              </div>
              <div class="col-6">
                <div class="text-muted small">Total Reports</div>
                <div class="fw-bold">{{ getTotalReportCount() }}</div>
              </div>
            </div>
            <hr class="my-2">
            <div class="row">
              <div class="col-6">
                <div class="text-muted small">Unique States</div>
                <div class="fw-bold">{{ getUniqueStateCount() }}</div>
              </div>
              <div class="col-6">
                <div class="text-muted small">Report Type</div>
                <div class="fw-bold">{{ getBatchReportTypeLabel() }}</div>
              </div>
            </div>
            <hr class="my-2">
            <div>
              <div class="text-muted small">Date Range</div>
              <div class="fw-bold">{{ formatDateRange(batchStartDate, batchEndDate) }}</div>
            </div>
          </div>
        </div>

        <div class="alert alert-info mb-0">
          <i class="bi bi-info-circle me-2"></i>
          Generate <strong>{{ getTotalReportCount() }}</strong> reports for
          <strong>{{ selectedOrganizations.size }}</strong> organizations across
          <strong>{{ getUniqueStateCount() }}</strong> unique states?
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelBatchConfirmation()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="generateBatch()"
          [disabled]="batchGenerating"
        >
          <span *ngIf="batchGenerating" class="spinner-border spinner-border-sm me-1"></span>
          Generate Reports
        </button>
      </div>
    </div>
  </div>
</div>
